import type { MenuProps } from "@chakra-ui/react";

const VIEWPORT_PADDING = 8;

/** Minimum width for CustomSelect option menus (must match MenuList minW). */
export const CUSTOM_SELECT_MENU_MIN_WIDTH = 200;

function getViewportWidth(): number {
  if (typeof window === "undefined") return 0;
  return window.visualViewport?.width ?? document.documentElement.clientWidth;
}

/**
 * Replaces Chakra's `matchWidth`: menu is at least CUSTOM_SELECT_MENU_MIN_WIDTH
 * wide (or as wide as the trigger when larger), then clamped inside the viewport.
 *
 * Chakra matchWidth + MenuList minW caused Popper to position using the narrow
 * trigger width while the rendered menu was wider - menus near the right edge
 * overflowed off-screen.
 */
function createCustomSelectMenuLayoutModifier(matchTriggerWidthOnly: boolean) {
  return {
  name: "customSelectMenuLayout",
  enabled: true,
  phase: "beforeWrite" as const,
  order: 900,
  requires: ["popperOffsets", "computeStyles"],
  fn(arg: any) {
    const { state } = arg;
    const referenceWidth = state.rects.reference.width;
    const menuWidth = matchTriggerWidthOnly
      ? referenceWidth
      : Math.max(referenceWidth, CUSTOM_SELECT_MENU_MIN_WIDTH);

    state.styles.popper = {
      ...state.styles.popper,
      width: `${menuWidth}px`,
      ...(matchTriggerWidthOnly
        ? {
            minWidth: `${menuWidth}px`,
            maxWidth: `${menuWidth}px`,
            overflow: "hidden",
          }
        : {}),
    };

    const offsets = state.modifiersData.popperOffsets;
    if (!offsets) return;

    const viewportWidth = getViewportWidth();
    if (viewportWidth <= 0) return;

    let x = offsets.x;
    if (x + menuWidth > viewportWidth - VIEWPORT_PADDING) {
      x = Math.max(
        VIEWPORT_PADDING,
        viewportWidth - VIEWPORT_PADDING - menuWidth,
      );
    }
    if (x < VIEWPORT_PADDING) {
      x = VIEWPORT_PADDING;
    }
    offsets.x = x;
  },
};
}

const customSelectMenuLayoutModifier = createCustomSelectMenuLayoutModifier(false);

type CustomSelectMenuPopperProps = Pick<
  MenuProps,
  "strategy" | "placement" | "flip" | "preventOverflow" | "gutter" | "modifiers"
>;

export function getCustomSelectMenuPopperProps(options?: {
  placement?: MenuProps["placement"];
  flip?: boolean;
  /** Menu width equals trigger width (never wider than the button). */
  matchTriggerWidthOnly?: boolean;
}): CustomSelectMenuPopperProps {
  const placement = options?.placement ?? "bottom-start";
  const flip = options?.flip ?? true;
  const matchTriggerWidthOnly = options?.matchTriggerWidthOnly ?? false;

  return {
    strategy: "fixed",
    placement,
    flip,
    preventOverflow: true,
    gutter: 8,
    modifiers: [
      {
        name: "preventOverflow",
        options: {
          rootBoundary: "viewport",
          padding: VIEWPORT_PADDING,
          altAxis: true,
          tether: flip,
        },
      },
      {
        name: "flip",
        enabled: flip,
        options: {
          fallbackPlacements: ["bottom-end", "top-start", "top-end"],
          padding: VIEWPORT_PADDING,
        },
      },
      createCustomSelectMenuLayoutModifier(matchTriggerWidthOnly),
    ],
  } as unknown as CustomSelectMenuPopperProps;
}

/**
 * Shared Popper settings for CustomSelect menus.
 * Keeps option lists inside the viewport when triggers sit near screen edges
 * (e.g. DatePicker year field in modals).
 */
export const CUSTOM_SELECT_MENU_POPPER_PROPS = getCustomSelectMenuPopperProps();
